import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import FeatherIcon from 'feather-icons-react';
import { useEOAFContext } from '../../context';
import '../PlanDeCuentaDropdown/PlanDeCuentaDropdown.scss';

/** Key and label for the "Saldos" section - excluded from category selection (ABONO/CARGO). */
const SALDOS_SECTION_KEY = '6';
const SALDOS_SECTION_LABEL = 'Saldos';

/**
 * Flatten EOAF structure: section headers = level1 > level2, selectable options = leaves.
 * Supports 4-level trees (leaf = level4 with empty children) and legacy 5-level (leaf under level4).
 * Excludes the "Saldos" category and all its children from the list (they must not be selectable for EOAF content).
 */
const flattenEOAFStructure = (tree) => {
  if (!Array.isArray(tree) || tree.length === 0) return [];
  const items = [];
  tree.forEach((level1) => {
    const isSaldos = level1?.key === SALDOS_SECTION_KEY || level1?.label === SALDOS_SECTION_LABEL;
    if (isSaldos) return;
    const children2 = level1?.children || [];
    children2.forEach((level2) => {
      const sectionKey = `${level1.key}-${level2.key}`;
      const sectionTitle = `${level1.label} > ${level2.label}`;
      items.push({
        key: `section-${sectionKey}`,
        value: null,
        text: sectionTitle,
        isSection: true,
        sectionKey,
      });
      const children3 = level2?.children || [];
      children3.forEach((level3) => {
        const children4 = level3?.children || [];
        children4.forEach((level4) => {
          const children5 = level4?.children || [];
          if (children5.length > 0) {
            children5.forEach((node) => {
              items.push({
                key: node.key,
                value: node.key,
                text: node.label,
                label: node.label,
                node,
                isSection: false,
                sectionKey,
              });
            });
          } else if (level4?.key) {
            // 4 niveles: la hoja es level4 (children: [])
            items.push({
              key: level4.key,
              value: level4.key,
              text: level4.label,
              label: level4.label,
              node: level4,
              isSection: false,
              sectionKey,
            });
          }
        });
      });
    });
  });
  return items;
};

const EOAFCategoryDropdown = ({
  companyId,
  value,
  onChange,
  placeholder = 'Seleccionar categoría',
  className = '',
  disabled = false,
  id,
  menuMaxHeight = 320,
  ...props
}) => {
  const eoafContext = useEOAFContext?.() ?? {};
  const structureTree = eoafContext?.structureTree ?? [];
  const fetching = eoafContext?.loading ?? false;
  const error = eoafContext?.error ?? null;
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [menuRect, setMenuRect] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const tree = Array.isArray(structureTree) ? structureTree : [];
  const allOptions = useMemo(() => flattenEOAFStructure(tree), [tree]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return allOptions;
    const query = searchQuery.toLowerCase();
    const filtered = [];
    let lastSectionKey = null;
    allOptions.forEach((option) => {
      if (option.isSection) {
        if (option.text.toLowerCase().includes(query)) {
          filtered.push(option);
          lastSectionKey = option.sectionKey;
        }
      } else {
        const matches =
          (option.text && option.text.toLowerCase().includes(query)) ||
          (option.key && option.key.toLowerCase().includes(query)) ||
          (option.label && option.label.toLowerCase().includes(query));
        if (matches) {
          if (option.sectionKey !== lastSectionKey) {
            const sectionHeader = allOptions.find(
              (opt) => opt.isSection && opt.sectionKey === option.sectionKey
            );
            if (sectionHeader) {
              filtered.push(sectionHeader);
              lastSectionKey = option.sectionKey;
            }
          }
          filtered.push(option);
        }
      }
    });
    return filtered;
  }, [allOptions, searchQuery]);

  useEffect(() => {
    if (value) {
      const selectedOption = allOptions.find((opt) => opt.value === value);
      if (selectedOption) setSelectedText(selectedOption.text);
      else setSelectedText('');
    } else {
      setSelectedText('');
    }
  }, [value, allOptions]);

  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) {
      setMenuRect(null);
      return;
    }
    const rect = dropdownRef.current.getBoundingClientRect();
    setMenuRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScrollOrResize = () => {
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        setMenuRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (option) => {
    setSelectedText(option.text);
    setIsOpen(false);
    setSearchQuery('');
    if (onChange) {
      onChange({}, {
        value: option.value,
        label: option.label,
        node: option.node,
      });
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
      inputRef.current?.blur();
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      const firstSelectable = filteredOptions.find((opt) => !opt.isSection);
      if (firstSelectable) handleSelect(firstSelectable);
    } else if (e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedText('');
    setIsOpen(false);
    setSearchQuery('');
    if (onChange) onChange({}, { value: '', label: '', node: null });
  };

  const displayValue = fetching && !selectedText
    ? 'Cargando categorías...'
    : isOpen
      ? searchQuery
      : selectedText;
  const showPlaceholder = !selectedText && !isOpen && !fetching;

  if (!companyId) {
    return (
      <div className={`plan-de-cuenta-dropdown ${className} disabled`}>
        <input
          type="text"
          readOnly
          className="plan-de-cuenta-dropdown-input"
          value=""
          placeholder={placeholder}
          disabled
        />
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className={`plan-de-cuenta-dropdown ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
      {...props}
    >
      <div className="plan-de-cuenta-dropdown-wrapper">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className="plan-de-cuenta-dropdown-input"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={showPlaceholder ? placeholder : ''}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        />
        {selectedText && !isOpen && !disabled && (
          <button
            type="button"
            className="plan-de-cuenta-dropdown-clear-button"
            onClick={handleClear}
            title="Limpiar"
            tabIndex={0}
          >
            <FeatherIcon icon="x" size={16} />
          </button>
        )}
        <span className="plan-de-cuenta-dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4" />
          </svg>
        </span>
      </div>
      {isOpen && !disabled && menuRect &&
        createPortal(
          <div
            ref={menuRef}
            className="plan-de-cuenta-dropdown-menu plan-de-cuenta-dropdown-menu-portal"
            style={{
              position: 'fixed',
              top: menuRect.top,
              left: menuRect.left,
              minWidth: Math.max(menuRect.width, 220),
              maxHeight: menuMaxHeight,
              zIndex: 999999,
            }}
          >
            {filteredOptions.length > 0 ? (
              <ul
                className="plan-de-cuenta-dropdown-options"
                role="listbox"
                style={{ maxHeight: `${menuMaxHeight}px` }}
              >
                {filteredOptions.map((option) => (
                  <li
                    key={option.key}
                    className={`plan-de-cuenta-dropdown-option ${option.isSection ? 'section-header' : ''} ${value === option.value ? 'selected' : ''}`}
                    onClick={() => !option.isSection && handleSelect(option)}
                    role={option.isSection ? 'presentation' : 'option'}
                    aria-selected={option.isSection ? undefined : value === option.value}
                  >
                    {option.isSection ? (
                      <span className="plan-de-cuenta-option-text">{option.text}</span>
                    ) : (
                      <span className="plan-de-cuenta-option-text">{option.label}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="plan-de-cuenta-dropdown-no-results">
                <div className="plan-de-cuenta-no-results-message">
                  {searchQuery.trim()
                    ? 'No se encontraron categorías que coincidan con la búsqueda'
                    : fetching
                      ? 'Cargando estructura EOAF...'
                      : 'Ingresa el código o nombre de la categoría para buscar'}
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default EOAFCategoryDropdown;
